"use client";

/**
 * PULSE Retrieval A/B Experiment Helpers
 *
 * - `usePulseVariant` reads the PostHog `pulse-retrieval` feature flag and maps
 *   it to the API `pulse_variant` query param value ("pulse" | "control").
 * - A module-level store remembers the variant from the most recent job-search
 *   API response so it can be attached as `pulse_variant` to AUD_JOB_* events.
 */

import { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import type { PulseVariant } from "@artha/utils";

/** PostHog feature flag key driving the PULSE retrieval experiment */
export const PULSE_RETRIEVAL_FLAG = "pulse-retrieval";

// Module-level store: variant reported by the most recent search response
// ("pulse" | "control" | "pulse_fallback"), undefined until a response is seen.
let lastSeenPulseVariant: string | undefined;

/**
 * Remember the variant from the most recent job-search API response
 * (`metadata.variant`). No-op when the response carries no variant.
 */
export function setLastSeenPulseVariant(variant?: string): void {
  if (variant) {
    lastSeenPulseVariant = variant;
  }
}

/**
 * Variant from the most recent job-search API response, or undefined if no
 * search response has been seen yet.
 */
export function getLastSeenPulseVariant(): string | undefined {
  return lastSeenPulseVariant;
}

/**
 * Map a PostHog feature-flag value to the API variant assignment.
 * Boolean flags: true → "pulse", false → "control".
 * Multivariate flags: "test"/"pulse" → "pulse", "control"/"bm25" → "control".
 * Anything else (flag not loaded / unknown key) → undefined.
 */
export function resolvePulseVariantFromFlag(
  value: string | boolean | undefined
): PulseVariant | undefined {
  if (value === true || value === "test" || value === "pulse") return "pulse";
  if (value === false || value === "control" || value === "bm25") return "control";
  return undefined;
}

/**
 * Read the PostHog `pulse-retrieval` feature flag as a PULSE variant.
 * Returns undefined until flags are loaded (or when the flag is not set),
 * in which case the API param must be omitted so the backend falls back to
 * its own hash bucketing.
 */
export function usePulseVariant(): { variant: PulseVariant | undefined; isLoaded: boolean } {
  const posthog = usePostHog();
  const [variant, setVariant] = useState<PulseVariant | undefined>(() => {
    if (typeof window !== "undefined" && posthog) {
      const val = posthog.getFeatureFlag?.(PULSE_RETRIEVAL_FLAG);
      return resolvePulseVariantFromFlag(val);
    }
    return undefined;
  });
  const [isLoaded, setIsLoaded] = useState(() => {
    if (typeof window === "undefined") return true;
    const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!apiKey) return true;
    const ph = posthog as any;
    if (ph && (ph.areFeatureFlagsLoaded?.() || ph.getFeatureFlag?.(PULSE_RETRIEVAL_FLAG) !== undefined)) {
      return true;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!apiKey) return;

    if (!posthog) return;

    const readFlag = () => {
      const value = posthog.getFeatureFlag?.(PULSE_RETRIEVAL_FLAG);
      setVariant(resolvePulseVariantFromFlag(value));
      setIsLoaded(true);
    };

    const ph = posthog as any;
    if (ph.areFeatureFlagsLoaded?.() || ph.getFeatureFlag?.(PULSE_RETRIEVAL_FLAG) !== undefined) {
      Promise.resolve().then(readFlag);
      return;
    }

    const timer = setTimeout(() => {
      readFlag();
    }, 200);

    const unsubscribe = posthog.onFeatureFlags?.(() => {
      clearTimeout(timer);
      readFlag();
    });

    return () => {
      clearTimeout(timer);
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [posthog]);

  return { variant, isLoaded };
}
